import { ParticipantTypeEnum } from "@/enums/RecapEnums";
import { AttendeeItem } from "@/interfaces/IRecap";
import { SearchOutlined } from "@ant-design/icons";
import { Person } from "@nexus/shared-ui";
import { Button, Divider, Flex, Input, Modal } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AttendanceTag from "../AttendanceTag";

const AttendeesModal = ({
  open,
  onClose,
  initialFilters = [],
  participants,
}: {
  open: boolean;
  onClose: () => void;
  initialFilters?: ParticipantTypeEnum[];
  participants?: any;
}) => {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState("");
  const [activeFilters, setActiveFilters] = useState<ParticipantTypeEnum[]>([]);

  const getUserImage = useMemo(
    () => (userId?: string) => {
      if (!userId) return "";

      return `${(
        window._env_?.VITE_BASE_API_URL || import.meta.env.VITE_BASE_API_URL
      ).replace("/meet/", "/admincenter/")}/v1/users/${userId}/image`;
    },
    []
  );

  useEffect(() => {
    if (open) {
      setActiveFilters(initialFilters);
    }
  }, [open, initialFilters]);

  const attendees = useMemo<AttendeeItem[]>(() => {
    return (participants?.data || []).map((item: any, index: number) => ({
      id: item?.userId || item?.user?.id || `${item?.nickname}-${index}`,
      fullName: item?.user?.fullName || item?.nickname || t("general.unknown"),
      description: item?.user?.jobTitle || "",
      avatar: getUserImage(item?.userId || item?.user?.id),
      status: item?.type || ParticipantTypeEnum.Absent,
    }));
  }, [participants, t, getUserImage]);

  const filterItems = useMemo(
    () => [
      {
        label: t("recap.attendees"),
        value: ParticipantTypeEnum.Attendee,
        count: attendees.filter(
          (item) => item?.status === ParticipantTypeEnum.Attendee
        ).length,
      },
      {
        label: t("recap.absents"),
        value: ParticipantTypeEnum.Absent,
        count: attendees.filter(
          (item) => item?.status === ParticipantTypeEnum.Absent
        ).length,
      },
      {
        label: t("recap.joinedViaCall"),
        value: ParticipantTypeEnum.JoinViaCall,
        count: attendees.filter(
          (item) => item?.status === ParticipantTypeEnum.JoinViaCall
        ).length,
      },
      {
        label: t("recap.guests"),
        value: ParticipantTypeEnum.Guest,
        count: attendees.filter(
          (item) => item?.status === ParticipantTypeEnum.Guest
        ).length,
      },
    ],
    [attendees, t]
  );

  const onToggleFilter = (value: ParticipantTypeEnum) => {
    setActiveFilters((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const onClearFilters = () => {
    setSearchValue("");
    setActiveFilters([]);
  };

  const onCloseModal = () => {
    onClearFilters();
    onClose();
  };

  const filteredAttendees = useMemo(() => {
    return attendees.filter((item) => {
      const search = searchValue.toLowerCase().trim();

      const matchesSearch = item?.fullName.toLowerCase().includes(search);

      const matchesFilter =
        activeFilters.length === 0
          ? true
          : activeFilters.includes(item?.status);

      return matchesSearch && matchesFilter;
    });
  }, [attendees, searchValue, activeFilters]);

  return (
    <Modal
      centered
      open={open}
      onCancel={onCloseModal}
      title={t("recap.attendance")}
      footer={
        <Flex justify="flex-end">
          <Button onClick={onCloseModal}>{t("actions.close")}</Button>
        </Flex>
      }
      rootClassName="recap-attendees-modal"
    >
      <Flex vertical gap={14}>
        <Input
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={t("recap.searchUser")}
          suffix={<SearchOutlined />}
        />

        <Flex gap={8} wrap="wrap">
          {filterItems.map((item) => {
            const isActive = activeFilters.includes(item?.value);

            return (
              <button
                key={item?.value}
                type="button"
                className={`recap-attendance-filter ${
                  isActive ? "active" : ""
                }`}
                onClick={() => onToggleFilter(item?.value)}
              >
                {item?.label}
                <span>{item?.count}</span>
              </button>
            );
          })}
        </Flex>

        <Flex vertical>
          {filteredAttendees.map((item, index) => (
            <div key={`${item?.id}-${index}`}>
              <Flex align="center" justify="space-between" gap={8}>
                <Flex className="recap-attendees-modal__person">
                  <Person
                    title={item?.fullName}
                    thumbUrl={item?.avatar}
                    description={item?.description || null}
                  />
                </Flex>

                <AttendanceTag value={item?.status} />
              </Flex>

              {index !== filteredAttendees.length - 1 && (
                <Divider className="my-3" />
              )}
            </div>
          ))}
        </Flex>
      </Flex>
    </Modal>
  );
};

export default AttendeesModal;
